import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";

function getUKNow() {
  const now = new Date(
    new Date().toLocaleString("en-US", {
      timeZone: "Europe/London"
    })
  );

  return {
    date: now.toISOString().split("T")[0],
    hour: now.getHours()
  };
}

const Analytics = ({ orders }) => {
  const [chartData, setChartData] = useState([]);
  const [hourRange, setHourRange] = useState(24);
  const [ukHour, setUkHour] = useState(getUKNow().hour);

  // Update UK hour every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setUkHour(getUKNow().hour);
    }, 60 * 1000);

    return () => clearInterval(timer);
  }, []);

  const formatHour12 = (hour) => {
    if (hour === 0) return "12 AM";
    if (hour === 12) return "12 PM";
    if (hour > 12) return `${hour - 12} PM`;
    return `${hour} AM`;
  };

  const to24HourFormat = (time12h) => {
    const [time, modifier] = time12h.toLowerCase().split(" ");
    let [hours, minutes] = time.split(":");

    if (modifier === "pm" && hours !== "12") hours = +hours + 12;
    if (modifier === "am" && hours === "12") hours = "00";

    return `${String(hours).padStart(2, "0")}:${minutes}`;
  };

  useEffect(() => {
    const { date: todayUK } = getUKNow();

    // Build buckets from 12 AM → current UK hour
    const hourlyData = Array.from({ length: ukHour + 1 }, (_, i) => ({
      hourIndex: i,
      hour: formatHour12(i),
      orders: 0,
      amount: 0
    }));

    if (Array.isArray(orders)) {
      orders.forEach(order => {
        if (!order.order_date || !order.order_time) return;
        if (order.order_date !== todayUK) return;

        const time24 = to24HourFormat(order.order_time);
        const hour = Number(time24.split(":")[0]); // UK hour

        if (hour <= ukHour) {
          hourlyData[hour].orders += 1;
          hourlyData[hour].amount += Number(order.total) || 0;
        }
      });
    }

    // Range filter
    let filtered = hourlyData;
    if (hourRange !== 24) {
      const start = Math.max(0, ukHour - hourRange + 1);
      filtered = hourlyData.filter(
        h => h.hourIndex >= start && h.hourIndex <= ukHour
      );
    }

    setChartData(filtered);
  }, [orders, hourRange, ukHour]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const { orders, amount } = payload[0].payload;
      return (
        <div style={{
          background: "#fff",
          padding: 10,
          borderRadius: 6,
          boxShadow: "0 2px 6px rgba(0,0,0,.2)"
        }}>
          <strong>{label}</strong>
          <div>🛒 Orders: {orders}</div>
          <div>💰 Amount: £{amount.toFixed(2)}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="mb-3">
        {[4, 6, 12, 24].map(h => (
          <button
            key={h}
            className={`btn btn-sm me-2 ${hourRange === h ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setHourRange(h)}
          >
            {h === 24 ? "Full Day" : `Last ${h} Hours`}
          </button>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" />
          <YAxis allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} />
          <Line dataKey="orders" stroke="#4f46e5" strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default Analytics;
